import {ReactNode, useCallback, useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {useGraphqlMutation} from "../../../../../amplify/hooks";
import {createOperationalEntityWithTenant as createOperationalEntityWithTenantMutation} from "../../../../../amplify/graphql";
import {
  CreateOperationalEntityWithTenantData,
  CreateOperationalEntityWithTenantVariables
} from "../../../../backend";
import {CreateNewEntityContext} from "./CreateNewEntityContext";
import {CreateNewEntityController} from "./CreateNewEntityController";
import {CreateNewEntityFormData} from "../../CreateNewEntityForm";
import {transformTestEntityToOperationalEntityInput} from "../utils/transforms";

interface TestEntityProviderProps {
  children: ReactNode;
  /**
   * Optional initial form data
   */
  initialData?: Partial<CreateNewEntityFormData>;
}

export function CreateNewEntityProvider({
  children,
  initialData
}: Readonly<TestEntityProviderProps>) {
  // GraphQL mutation for creating operational entity with tenant
  const [createOperationalEntityWithTenant, {loading: isMutationLoading}] =
    useGraphqlMutation<
      CreateOperationalEntityWithTenantData,
      CreateOperationalEntityWithTenantVariables
    >({
      amplifyMutation: createOperationalEntityWithTenantMutation
    });

  // Form data state
  const [formData, setFormData] = useState<CreateNewEntityFormData>(() => ({
    tenantChoice: initialData?.tenantChoice || "",
    tenantNewName: initialData?.tenantNewName || "",
    tenantExistingId: initialData?.tenantExistingId || "",
    dbaName: initialData?.dbaName || "",
    legalName: initialData?.legalName || "",
    status: initialData?.status || "",
    telecomNumbers: initialData?.telecomNumbers || "",
    streetAddressLine1: initialData?.streetAddressLine1 || "",
    streetAddressLine2: initialData?.streetAddressLine2 || "",
    city: initialData?.city || "",
    state: initialData?.state || "",
    country: initialData?.country || "",
    zip: initialData?.zip || ""
  }));

  // Form validation state
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(null);
  const [entityStatus, setEntityStatus] = useState<
    "idle" | "queued" | "in-progress" | "created" | "failed"
  >("idle");

  // Form data change handler
  const onFormDataChange = useCallback(
    (data: CreateNewEntityFormData) => {
      setFormData(data);
      // Clear previous submission state when form data changes
      if (submitError || isSubmitSuccessful) {
        setSubmitError(null);
        setIsSubmitSuccessful(false);
        setCreatedEntityId(null);
        setEntityStatus("idle");
      }
    },
    [submitError, isSubmitSuccessful]
  );

  // Form validation change handler
  const onValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  // Form submit handler (validation only)
  const onFormSubmit = useCallback(async () => {
    if (!isFormValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Form validation successful - navigation will be handled by the stepper
      setIsSubmitSuccessful(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setSubmitError(`Failed to validate form: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [isFormValid, isSubmitting]);

  // Entity creation handler (actual GraphQL mutation)
  const onCreateEntity = useCallback(async () => {
    if (!isFormValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setEntityStatus("queued");

    try {
      // Set status to in-progress and create the entity
      setEntityStatus("in-progress");

      // Generate transaction ID for GraphQL mutation
      const transactionId = uuidv4();

      // Transform form data to GraphQL input format (with tenantId)
      const operationalEntityInput =
        transformTestEntityToOperationalEntityInput(formData, transactionId);

      // Execute GraphQL mutation with tenant
      const result = await createOperationalEntityWithTenant({
        variables: {
          createOEInput: operationalEntityInput
        }
      });

      if (result.data?.createOEResult?.id) {
        // Success: Entity was created, store the actual entity ID from backend
        setCreatedEntityId(result.data.createOEResult.id);
        setEntityStatus("created");
        setIsSubmitSuccessful(true);
      } else {
        // Handle case where mutation succeeded but returned no data
        throw new Error("Entity creation failed: No data returned from server");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setSubmitError(`Failed to create test entity: ${errorMessage}`);
      setEntityStatus("failed");
    } finally {
      setIsSubmitting(false);
    }
  }, [isFormValid, isSubmitting, formData, createOperationalEntityWithTenant]);

  // Clear submission state handler
  const clearSubmissionState = useCallback(() => {
    setSubmitError(null);
    setIsSubmitSuccessful(false);
    setCreatedEntityId(null);
    setEntityStatus("idle");
  }, []);

  // Combine local loading state with Apollo mutation loading
  const combinedIsSubmitting = isSubmitting || isMutationLoading;

  // Create controller object with memoization for performance
  const controller: CreateNewEntityController = useMemo(
    () => ({
      formData,
      isFormValid,
      onFormDataChange,
      onValidationChange,
      onFormSubmit,
      onCreateEntity,
      isSubmitting: combinedIsSubmitting,
      submitError,
      isSubmitSuccessful,
      createdEntityId,
      entityStatus,
      clearSubmissionState
    }),
    [
      formData,
      isFormValid,
      onFormDataChange,
      onValidationChange,
      onFormSubmit,
      onCreateEntity,
      combinedIsSubmitting,
      submitError,
      isSubmitSuccessful,
      createdEntityId,
      entityStatus,
      clearSubmissionState
    ]
  );

  return (
    <CreateNewEntityContext.Provider value={controller}>
      {children}
    </CreateNewEntityContext.Provider>
  );
}
